// GENERATED FILE — DO NOT EDIT.
// Source of truth: config/retention-schedule.json
// Regenerate: node scripts/generate-retention-schedule.mjs
//
// This schedule is PUBLISHED at /trust and is the document src/pages/Privacy.tsx
// points at when it says "documented retention schedules". Every fact in it is
// bound to the code by scripts/check-retention-schedule.mjs, so it is generated
// from that SSOT rather than typed into the page by hand.

export interface StorageRetention {
  /** The storage family as the one door mints it (functions/src/storage/objectPaths.ts). */
  id: string;
  /** What it is, in words a customer uses. */
  label: string;
  /** How long we keep it. */
  retention: string;
  /** What makes it go away. */
  deletedBy: string;
  /** Why it is kept that way. */
  reason: string;
}

export interface RetentionWindow {
  id: string;
  days: number;
  /** Where this same number is published in the privacy policy. */
  published: string;
  reason: string;
}

export interface RetainedRecord {
  record: string;
  period: string;
}

/** The date this schedule was last reviewed against the running system. */
export const RETENTION_LAST_REVIEWED = "2026-09-12";

/** Every storage family the platform mints, and how long each is kept. */
export const STORAGE_RETENTION: readonly StorageRetention[] = [
  {
    id: "turns",
    label: "files and deliverables in a conversation",
    retention: "the life of the account",
    deletedBy: "a verified deletion request",
    reason: "Conversation attachments and the deliverables generated in a conversation. This is the customer's own work product; a timer on it destroys matter files a firm is relying on.",
  },
  {
    id: "userAttachments",
    label: "files you upload into a conversation",
    retention: "the life of the account",
    deletedBy: "a verified deletion request",
    reason: "Files a person uploaded into a conversation. Same reasoning as turns — theirs, not ours to expire.",
  },
  {
    id: "ingest",
    label: "documents you upload for reading, and what was read out of them",
    retention: "the life of the account",
    deletedBy: "a verified deletion request",
    reason: "Uploaded documents AND their extraction results. The results are referenced by the conversation that produced them, so expiring this prefix would leave threads pointing at nothing.",
  },
  {
    id: "widgetReports",
    label: "PDFs produced for a visitor through a public widget",
    retention: "the life of the account",
    deletedBy: "a verified deletion request",
    reason: "PDFs produced for a visitor through the public widget. For a visitor who never made an account this may be the only copy they have.",
  },
  {
    id: "dataExports",
    label: "copies of your own data that you asked us to produce",
    retention: "the life of the account",
    deletedBy: "a verified deletion request",
    reason: "When you ask for a copy of your data we build one and hand you a link to it. That copy is the most concentrated collection of your information there is, so it is listed here rather than treated as a by-product: a deletion request reaches it like everything else, and the link we give you expires within the hour.",
  },
  {
    id: "browserProfile",
    label: "saved browser sign-ins on your workspace machine",
    retention: "the life of the workstation",
    deletedBy: "a verified deletion request, or the workstation being retired",
    reason: "Archived browser profiles carrying an organization's signed-in sessions. Expiring one signs the organization out of everything it connected.",
  },
  {
    id: "readAloud",
    label: "audio of a message read aloud",
    retention: "the life of the account",
    deletedBy: "a verified deletion request",
    reason: "Speech rendered from a message. It is a content-addressed CACHE and is regenerable — but a readout is paid for once and every later listener is free (R9), so deleting one re-bills the next person who presses play. Regenerable is not the same as free to discard.",
  },
];

/** The only retention with a countdown behind it. */
export const RETENTION_WINDOWS: readonly RetentionWindow[] = [
  {
    id: "guestConversations",
    days: 30,
    published: "src/pages/Privacy.tsx and src/pages/Trust.tsx — '30 days after your last message'",
    reason: "A signed-out visitor's conversation, kept so it is still there when they come back and can be carried into an account if they sign up. Keyed to an irreversible hash of the IP address; the address itself is never stored.",
  },
];

/** Records the law requires be kept, and for how long. */
export const RETAINED_RECORDS: readonly RetainedRecord[] = [
  { record: "billing and audit records", period: "7 years from the end of the billing relationship" },
  { record: "automatic-renewal consent", period: "4 years" },
];

/** How many database locations are purged, kept, or stripped of identifiers. */
export const DISPOSITION_COUNTS = {
  purge: 88,
  retain: 12,
  anonymize: 5,
} as const;

/** Named out loud, because a schedule listing only timers reads as if everything has one. */
export const NOT_TIME_LIMITED: readonly string[] = [
  "Customer work product in every storage family above — your files, filings and deliverables, kept for the life of the account and deleted when you ask.",
  "Account, workspace and membership records, for as long as the account exists.",
  "Anonymised totals, which carry no identifier to retain.",
];

/**
 * The same facts as bare prose lines, for the public-site index the signed-out
 * Prest0n searches (D13). Not rendered — the page renders the structured
 * exports above. See the generator for why this shape exists.
 */
export const RETENTION_LINES: readonly string[] = [
  "We keep files and deliverables in a conversation for the life of the account. Deleted by: a verified deletion request.",
  "We keep files you upload into a conversation for the life of the account. Deleted by: a verified deletion request.",
  "We keep documents you upload for reading, and what was read out of them for the life of the account. Deleted by: a verified deletion request.",
  "We keep PDFs produced for a visitor through a public widget for the life of the account. Deleted by: a verified deletion request.",
  "We keep copies of your own data that you asked us to produce for the life of the account. Deleted by: a verified deletion request.",
  "We keep saved browser sign-ins on your workspace machine for the life of the workstation. Deleted by: a verified deletion request, or the workstation being retired.",
  "We keep audio of a message read aloud for the life of the account. Deleted by: a verified deletion request.",
  "Customer work product — your files, filings and deliverables — is kept for the life of the account and deleted when you ask. It is deliberately not on a timer: a schedule that deleted a law firm’s matter files would destroy work people are relying on.",
  "A signed-out visitor’s conversation is kept for 30 days, then deleted automatically. It is keyed to an irreversible hash of the IP address, and the address itself is never stored.",
  "We keep billing and audit records for 7 years from the end of the billing relationship, because the law requires it.",
  "We keep automatic-renewal consent for 4 years, because the law requires it.",
  "Records the law requires us to keep are the one exception to deletion on request. CCPA §1798.105(d) carves them out, so keeping them is compliance with a legal obligation rather than a refusal. Everything else in the account still goes.",
  "Our retention schedule was last reviewed on 2026-09-12.",
];
